/**
 * Project + Conversation — primary navigation nouns.
 *
 * In v1 conversations were the only first-class object; v2 introduces
 * ``Project`` as the container (multiple conversations + canvases share a
 * workspace).
 *
 * The Project schema mirrors the wire shape from
 * ``GET /api/v1/projects`` (see ``api/endpoints/projects.ts``). The full
 * ``Project`` type is re-exported from ``@ando/core/api`` — keeping it
 * here as a zod schema gives runtime guards for callers that need to
 * parse arbitrary payloads (e.g. proactive nudge events that reference
 * a project by row). Apps importing the bare TypeScript type can prefer
 * the ``@ando/core/api`` re-export to avoid the zod dependency.
 */

import { z } from "zod";
import { IsoTimestampSchema, UuidSchema, WorkspaceCategorySchema } from "./common.js";

export const ProjectSchema = z.object({
  project_id: UuidSchema,
  name: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  description: z.string().nullable(),
  default_folder_filter: z.array(z.string()).readonly(),
  default_connection_ids: z.array(z.string()).readonly(),
  default_agent_mode_version_id: UuidSchema.nullable(),
  position: z.number().int().nonnegative(),
  is_archived: z.boolean(),
  // Per-project persona — Phase 1 ecosystem foundation (June 7, 2026).
  // ``instructions`` is appended to the agent system prompt at every
  // turn. ``persona_role`` / ``persona_tone`` are UX hints stored
  // alongside for round-tripping; the BE does NOT feed them into the
  // prompt directly. ``.optional()`` guards a transient cross-deploy
  // window where the FE bundle is newer than the BE response.
  instructions: z.string().nullable().optional(),
  persona_role: z.string().nullable().optional(),
  persona_tone: z.string().nullable().optional(),
  // Phase 2.5 HyperMem cross-project transfer policy (June 8, 2026).
  // ``isolated`` keeps the project's memory siloed; ``shared`` puts it
  // in the cross-project pool so other ``shared`` projects of the same
  // user can recall its facts (and vice versa). Defaults to
  // ``isolated`` server-side — the ``.default()`` keeps legacy /
  // pre-Phase-2.5 responses parseable.
  cross_project_transfer_policy: z
    .enum(["isolated", "shared"])
    .default("isolated"),
  created_at: IsoTimestampSchema,
  updated_at: IsoTimestampSchema,
  conversation_count: z.number().int().nonnegative(),
  // ── Live activity fields (June 8, 2026 — UX "feel alive" pass) ──────
  // Populated by the BE joined subqueries in ``list_projects`` /
  // ``get_project``. All optional so the contract stays additive: an
  // older BE response (pre-pass) parses identically. The Hub + Detail
  // FE surfaces consume these to build narrative copy.
  last_conversation_id: z.string().nullable().optional(),
  last_conversation_title: z.string().nullable().optional(),
  last_active_at: z.string().nullable().optional(),
  overdue_tasks_count: z.number().int().nonnegative().default(0),
  pending_tasks_count: z.number().int().nonnegative().default(0),
  memory_count: z.number().int().nonnegative().default(0),
  last_memory_at: z.string().nullable().optional(),
  last_artifact_filename: z.string().nullable().optional(),
  last_artifact_at: z.string().nullable().optional(),
  next_run_at: z.string().nullable().optional(),
  active_flows_count: z.number().int().nonnegative().default(0),
});
export type Project = z.infer<typeof ProjectSchema>;

export const ConversationSchema = z.object({
  id: UuidSchema,
  title: z.string().nullable().optional(),
  user_id: UuidSchema,
  project_id: UuidSchema.nullable().optional(),
  workspace_category: WorkspaceCategorySchema.optional(),
  is_archived: z.boolean().optional(),
  is_pinned: z.boolean().optional(),
  agent_mode_id: UuidSchema.nullable().optional(),
  created_at: IsoTimestampSchema,
  updated_at: IsoTimestampSchema,
});
export type Conversation = z.infer<typeof ConversationSchema>;
